

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "@/hooks/useSearchParams";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import {
  handleMfaSetup,
  handleMfaConfirmSetup,
  cancelMfaFlow,
} from "@/lib/api/handlers/AuthenticationHandling";
import { useAuth } from "@/lib/auth/AuthContext";
import { hydrateAccountPresentation } from "@/lib/auth/hydrateAccountPresentation";
import { isSwitchAccountIntent, safeNext, withNext } from "@/lib/auth/safeNext";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";

const MAX_ATTEMPTS = 5;

interface MfaSetupState {
  step: 1 | 2 | 3 | 4;
  secret: string;
  otpAuthUri: string;
  otp: string[];
  recoveryCodes: string[];
  codesSaved: boolean;
  error: string;
  isLoading: boolean;
  activeInput: number;
  timeRemaining: number;
  sessionWarning: boolean;
  isAuthenticated: boolean;
  attempts: number;
  copyCodesState: "idle" | "copied" | "error";
  downloadCodesState: "idle" | "done";
  // Forced-setup flow only: backend returns companyId on confirm. Stored
  // here so handleComplete() can flip AuthContext state before navigation
  // — see [[feedback_uniformity_over_migration_cost]] re: routing all
  // auth-state mutations through useAuth.
  companyId: string | null;
  userId: string | null;
}

export function useMfaSetupForm(initialTimeRemaining: number) {
  const [state, setState] = useState<MfaSetupState>({
    step: 1,
    secret: "",
    otpAuthUri: "",
    otp: Array(6).fill(""),
    recoveryCodes: [],
    codesSaved: false,
    error: "",
    isLoading: false,
    activeInput: 0,
    timeRemaining: initialTimeRemaining,
    sessionWarning: initialTimeRemaining <= 60 && initialTimeRemaining > 0,
    isAuthenticated: false,
    attempts: 0,
    copyCodesState: "idle",
    downloadCodesState: "idle",
    companyId: null,
    userId: null,
  });

  const navigate = useNavigate();
  const { markAuthenticated } = useAuth();
  // The login deep link (forced setup after login) or the page that hit
  // `mfaRequired` mid-session (client.ts) — handed over by URL and validated
  // again here (billing-comms Teil 2a).
  const searchParams = useSearchParams();
  const next = safeNext(searchParams.get("next"));
  // "Sign in with another account" (billing-comms fix r1, X1): the login form
  // hands the exact marker on; it lifts the account-switch rule, never safeNext.
  const switching = isSwitchAccountIntent(searchParams);
  const t = useTranslations("mfaSetup");
  const tErrors = useTranslations("errors");
  const cancellingRef = useRef(false);

  const updateState = (newState: Partial<MfaSetupState>) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  // Session countdown timer — stops once authenticated (step 4)
  useEffect(() => {
    if (state.timeRemaining <= 0 || state.isAuthenticated) return;

    const interval = setInterval(() => {
      setState((prev) => {
        if (prev.isAuthenticated) return prev;
        const next = prev.timeRemaining - 1;
        if (next <= 0) {
          return { ...prev, timeRemaining: 0, sessionWarning: false, error: t("sessionExpired") };
        }
        if (next <= 60 && !prev.sessionWarning) {
          return { ...prev, timeRemaining: next, sessionWarning: true };
        }
        return { ...prev, timeRemaining: next };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [state.timeRemaining > 0, state.isAuthenticated]);

  // Auto-cancel when timer hits 0 (only if not authenticated)
  useEffect(() => {
    if (state.timeRemaining === 0 && !state.isAuthenticated && !cancellingRef.current) {
      cancellingRef.current = true;
      const timeout = setTimeout(() => handleCancel(), 2000);
      return () => clearTimeout(timeout);
    }
  }, [state.timeRemaining, state.isAuthenticated]);

  useEffect(() => {
    if (state.step !== 3) return;
    const inputElement = document.getElementById(
      `otp-input-${state.activeInput}`,
    );
    if (inputElement) {
      (inputElement as HTMLInputElement).focus();
    }
  }, [state.activeInput, state.step]);

  async function handleStart() {
    updateState({ isLoading: true, error: "" });
    try {
      const response = await handleMfaSetup();

      if (response && typeof response === "object" && "isServerError" in response) {
        throwServerError(response);
      }

      updateState({
        step: 2,
        secret: response.secret,
        otpAuthUri: response.otpAuthUri,
        isLoading: false,
      });
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      if (errorMessage === "mfaSessionExpired") {
        updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isLoading: false });
        setTimeout(() => handleCancel(), 2000);
        return;
      }

      updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isLoading: false });
    }
  }

  function handleScanned() {
    updateState({ step: 3, error: "", otp: Array(6).fill(""), activeInput: 0 });
  }

  async function handleVerify(event: React.FormEvent) {
    event.preventDefault();

    if (state.attempts >= MAX_ATTEMPTS) return;

    if (state.otp.some((digit) => digit === "")) {
      updateState({ error: t("invalidCode") });
      return;
    }

    const code = state.otp.join("");
    updateState({ error: "", isLoading: true });

    try {
      const response = await handleMfaConfirmSetup(code);

      if (response && typeof response === "object" && "isServerError" in response) {
        throwServerError(response);
      }

      updateState({
        step: 4,
        recoveryCodes: response.recoveryCodes,
        // Forced-setup flow returns companyId; in-settings flow doesn't.
        // We stash here and apply `markAuthenticated` in `handleComplete()`
        // so the user finishes acknowledging recovery codes before
        // PrivateRoute lets them past `/auth/mfa-setup`.
        companyId: response.companyId ?? null,
        userId: response.userId ?? null,
        isLoading: false,
        isAuthenticated: true,
        sessionWarning: false,
      });
    } catch (error) {
      let errorMessage = "unknown";
      if (error instanceof Error) errorMessage = error.message;

      const newAttempts = state.attempts + 1;

      if (errorMessage === "mfaSessionExpired") {
        updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isLoading: false });
        setTimeout(() => handleCancel(), 2000);
        return;
      }

      if (newAttempts >= MAX_ATTEMPTS) {
        updateState({
          error: t("tooManyAttempts"),
          isLoading: false,
          attempts: newAttempts,
        });
        setTimeout(() => handleCancel(), 3000);
        return;
      }

      updateState({ error: translateBackendError(tErrors, error instanceof Error ? error : "unknown"), isLoading: false, attempts: newAttempts });
    }
  }

  function handleDownloadCodes() {
    const content = state.recoveryCodes.join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    updateState({ downloadCodesState: "done" });
    setTimeout(() => updateState({ downloadCodesState: "idle" }), 2000);
  }

  async function handleCopyCodes() {
    try {
      await navigator.clipboard.writeText(state.recoveryCodes.join("\n"));
      updateState({ copyCodesState: "copied" });
      setTimeout(() => updateState({ copyCodesState: "idle" }), 2000);
    } catch {
      updateState({ copyCodesState: "error" });
      setTimeout(() => updateState({ copyCodesState: "idle" }), 2000);
    }
  }

  async function handleCancel() {
    window.dispatchEvent(new Event("navigationStart"));
    try {
      await cancelMfaFlow();
    } catch {
      // Cookie cleanup failed — navigate anyway
    }
    navigate(withNext("/auth/login", next, { switchAccount: switching }));
  }

  async function handleComplete() {
    // Forced-setup flow: now that the user has acknowledged recovery
    // codes, flip AuthContext state so `/` doesn't bounce back via
    // PrivateRoute. In-settings flow has no companyId (user already
    // authenticated) — skip.
    let landing = next ?? "/";
    if (state.companyId) {
      updateState({ isLoading: true });
      // The confirm-setup response carries the userId too — it just used to be
      // dropped on the way here. Passing it keeps the identity stamp in ONE
      // namespace (always a userId) and finally gives this lane a
      // `veltric.userId`, which every "is this row me" check needs.
      const hydrated = await hydrateAccountPresentation({ userId: state.userId ?? undefined });
      // A different account than the browser's last one: ignore `next`,
      // unless the switch was asked for (`intent=switch`).
      if (hydrated?.accountSwitched === true && !switching) landing = "/";
      markAuthenticated(state.companyId, state.userId ?? undefined);
    }
    window.dispatchEvent(new Event("navigationStart"));
    navigate(landing);
  }

  function handleChange(value: string, index: number) {
    const newOtp = [...state.otp];
    newOtp[index] = value.slice(-1);

    const updates: Partial<MfaSetupState> = { otp: newOtp, error: "" };

    if (value && index < 5) {
      updates.activeInput = index + 1;
    }

    updateState(updates);
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Backspace" && !state.otp[index] && index > 0) {
      updateState({ activeInput: index - 1 });
    }
  };

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const pastedData = event.clipboardData
      .getData("text")
      .replaceAll(" ", "")
      .slice(0, 6);

    const newOtp = [...state.otp];
    pastedData.split("").forEach((char, i) => {
      newOtp[i] = char;
    });

    const lastIndex = Math.min(pastedData.length - 1, 5);
    updateState({
      otp: newOtp,
      error: "",
      activeInput: lastIndex >= 0 ? lastIndex : 0,
    });
  }

  return {
    step: state.step,
    secret: state.secret,
    otpAuthUri: state.otpAuthUri,
    otp: state.otp,
    recoveryCodes: state.recoveryCodes,
    codesSaved: state.codesSaved,
    error: state.error,
    isLoading: state.isLoading,
    activeInput: state.activeInput,
    timeRemaining: state.timeRemaining,
    sessionWarning: state.sessionWarning,
    attempts: state.attempts,
    copyCodesState: state.copyCodesState,
    downloadCodesState: state.downloadCodesState,
    handleStart,
    handleScanned,
    handleVerify,
    handleDownloadCodes,
    handleCopyCodes,
    handleComplete,
    handleCancel,
    handleChange,
    handleKeyDown,
    handlePaste,
    updateState,
  };
}
